import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { delegateTasksForStaff } from 'api/Wizard/Wizard';

import { optionify } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import Modal from 'components/shared/Modal/Modal';
import Select from 'components/shared/Select/Select';
import Textarea from 'components/shared/Textarea/Textarea';

const TaskDelegationModal = ({
  isOpen,
  handleClose,
  instanceId,
  step_ids = [],
  substep_ids = [],
  assignments,
  practice,
  onDelegateSuccess
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const navigate = useNavigate();

  const usersList = optionify(practice?.members, 'professionalFullName');

  const selectPractitioners = (e) => {
    setSelectedUsers(e);
  };

  const stateCleanup = () => {
    setSelectedUsers([]);
    setNote('');
    setDueDate(null);
  };

  const { mutateAsync, isLoading: isDelegating } = useCustomMutation({
    mutationFn: (data) => delegateTasksForStaff(navigate, data),
    invalidateQueryKey: ['wizardInstanceDelegation', 'wizardInstance']
  });

  const handleChangeEndOnDate = (date) => {
    setDueDate(date);
  };

  const originalNote = assignments?.[0]?.note || '';
  const originalDueDate = assignments?.[0]?.due_date || null;
  const detailsChanged = note !== originalNote || dueDate !== originalDueDate;

  const handleSubstepDelegation = async () => {
    const currentUserIds = selectedUsers.map((u) => u.value);
    const originalUserIds = (assignments || []).map((a) => a.user_id);

    const usersToAdd = currentUserIds.filter((id) => !originalUserIds.includes(id));
    const usersToRemove = originalUserIds.filter((id) => !currentUserIds.includes(id));
    const usersUnchanged = currentUserIds.filter((id) => originalUserIds.includes(id));

    if (usersToAdd.length === 0 && usersToRemove.length === 0 && !detailsChanged) {
      handleClose();
      return;
    }

    const basePayload = {
      wizard_instance_id: instanceId,
      practice_id: practice.id,
      step_ids,
      substep_ids
    };

    if (usersToRemove.length > 0) {
      await mutateAsync({ ...basePayload, userIds: usersToRemove, operation: 'delete' });
    }

    const usersToUpsert = detailsChanged ? [...usersUnchanged, ...usersToAdd] : usersToAdd;

    if (usersToUpsert.length > 0) {
      await mutateAsync({
        ...basePayload,
        userIds: usersToUpsert,
        note,
        due_date: dueDate,
        operation: 'add'
      });
    }

    stateCleanup();
    onDelegateSuccess?.();
    handleClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    if (assignments && assignments.length > 0) {
      setSelectedUsers(
        assignments.map((a) => ({
          value: a.user_id,
          label: a.first_name + ' ' + a.last_name
        }))
      );
      setNote(assignments?.[0]?.note || '');
      setDueDate(assignments?.[0]?.due_date || null);
    } else {
      stateCleanup();
    }
  }, [isOpen, assignments]);

  useEffect(() => {
    return () => {
      stateCleanup();
    };
  }, []);

  const originalUserIds = (assignments || []).map((a) => a.user_id);
  const currentUserIds = selectedUsers.map((u) => u.value);
  const hasRemovals = originalUserIds.some((id) => !currentUserIds.includes(id));
  const hasAdditions = currentUserIds.some((id) => !originalUserIds.includes(id));
  const hasChanges = hasRemovals || hasAdditions || detailsChanged;

  const isFormValid = hasChanges && (selectedUsers.length === 0 ? hasRemovals : Boolean(dueDate));

  return (
    <Modal
      title="Delegate this task"
      slideFromRight
      menuPortalTarget={document.body}
      isOpen={isOpen}
      footer={
        <div className="flex gap-4">
          <Button
            text="Close"
            color="neutral"
            onClick={handleClose}
            size="small"
            style="outlined"
          />
          <Button
            text={
              hasRemovals && !hasAdditions && !detailsChanged
                ? 'Remove'
                : originalUserIds.length > 0
                  ? 'Update'
                  : 'Delegate'
            }
            color={hasRemovals && !hasAdditions && !detailsChanged ? 'danger' : 'primary'}
            onClick={handleSubstepDelegation}
            size="small"
            isDisabled={!isFormValid}
            isLoading={isDelegating}
          />
        </div>
      }
      handleClose={handleClose}>
      <Select
        inputId="staff"
        value={selectedUsers}
        options={usersList}
        onChange={selectPractitioners}
        isMulti
        required
        label="Staff member"
        placeholder="Select staff member"
        className="mb-3"
      />
      <Textarea
        textareaClassName="min-h-[100px]"
        placeholder="Enter any extra details or comments related to this task"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        label="Additional notes"
        className="mb-3"
      />
      <DateInputPicker
        label="Due Date"
        dateFormat="MM/dd/yyyy"
        type="string"
        onChange={handleChangeEndOnDate}
        required
        value={dueDate}
      />
    </Modal>
  );
};

export default TaskDelegationModal;
